import {NavigationEnd, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {AuthService, ConfigurationService, LoginDetails} from "@fedeltapointofsale/data-client";
import {UserProfileSummaryData} from "../user-profile/user-profile-summary.data";
import {Config} from "../config";
import {FUIBreakpoints, FUIResponsiveService} from "@fedeltapointofsale/ui";
import {AbstractMessaging} from "./customer-support-messaging.service";

declare let window: any;

export class IntercomMessaging implements AbstractMessaging {

    /**
     * Indicates that the Intercom javascript has been initialised ready for use
     */
    private initialised: boolean;

    /**
     * Indicates if intercom has been enabled via preference
     */
    private intercomEnabled: boolean;

    /**
     * Indicates that a user session has started
     */
    private started: boolean;

    /**
     * Intercom App Id. This is unique per workspace
     */
    private intercomAppId: string;

    /**
     * Derived Id to globally identify a user
     */
    private userId: string;

    /**
     * WBO client Id. Should be the same name as the sub-domain. This is used to identify the company
     */
    private clientId: string;

    /**
     * Indicates if the JavaScript has been injected
     */
    private jsInjected: boolean;

    /**
     * Indicates if the intercom icon is hidden
     */
    public hidden: boolean;

    /**
     * User Profile information collected after a login
     */
    private pendingUserUpdate: UserProfileSummaryData;

    private subscriptions: Subscription = new Subscription();

    constructor(private router: Router,
                private authService: AuthService,
                private configurationService: ConfigurationService,
                private responsiveService: FUIResponsiveService,
                private wboVersion: string) {


        if (this.responsiveService.breakpointApplies(FUIBreakpoints.smallDown)) {
            this.hidden = true;
        }

        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(() => {
                if (this.responsiveService.breakpointApplies(FUIBreakpoints.smallDown)) {
                    this.hide();
                } else {
                    this.show();
                }
            }
        ));

        this.subscriptions.add(this.router.events.subscribe((e) => {
            if (e instanceof NavigationEnd) {
                this.update();
            }
        }));

        this.subscriptions.add(this.authService.loginDetails$.subscribe((l: LoginDetails) => {
            if (l.user) {
                setTimeout(() => this.init());
                this.startUserSession();
            } else {
                this.endUserSession();
            }
        }));
    }

    onDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Initialise by loading the Intercom javascript
     */
    public async init(): Promise<void> {
        if (this.jsInjected) {
            return;
        }

        this.clientId = await this.configurationService.getString(Config.CLIENT_ID);
        if (!this.clientId) {
            return;
        }

        this.intercomEnabled = await this.configurationService.getBoolean(Config.INTERCOM_ENABLED);
        if (!this.intercomEnabled) {
            return;
        }


        this.intercomAppId = await this.configurationService.getString(Config.INTERCOM_APP_ID);
        if (!this.intercomAppId) {
            return;
        }

        this.jsInjected = true;
        const s = document.createElement("script");
        s.type = "text/javascript";
        s.async = true;
        s.src = "https://widget.intercom.io/widget/" + this.intercomAppId;
        document.getElementsByTagName("head")[0].appendChild(s).onload = () => {
            console.log("Intercom loaded");
            this.initialised = true;
            this.startUserSession();
        };
    }

    /**
     * Indicates if the messaging service widget is displayed.
     */
    public isDisplayed(): boolean {
        return !this.hidden && !!this.intercomAppId;
    }

    /**
     * Sends an "update" event to intercom. This should be called when routing to different pages
     */
    public update() {
        if (this.started) {
            window.Intercom("update");
        }
    }

    /**
     * Sends an event that occurred
     * @param {string} event Name of the event
     */
    public trackEvent(event: string) {
        if (this.started) {
            window.Intercom("trackEvent", event);
        }
    }

    /**
     * Shows the customer support control
     */
    public show() {
        if (this.initialised && this.hidden) {
            this.hidden = false;
            window.Intercom("update", {hide_default_launcher: false});
        }
    }

    /**
     * Hides the customer support control
     */
    public hide() {
        if (this.initialised && !this.hidden) {
            this.hidden = true;
            window.Intercom("update", {hide_default_launcher: true});
        }
    }


    public updateUserDetails(userProfileSummary: UserProfileSummaryData): void {
        if (!userProfileSummary || !userProfileSummary.userId) {
            this.endUserSession();
        } else {
            this.pendingUserUpdate = userProfileSummary;
            this.startUserSession();
        }
    }

    /**
     * Boots intercom and starts a session
     */
    private startUserSession(): void {
        if (this.initialised && this.pendingUserUpdate) {
            this.userId = this.clientId + "-" + this.pendingUserUpdate.userId;
            const accessRole = this.pendingUserUpdate.accessRole && this.pendingUserUpdate.accessRole.name ? this.pendingUserUpdate.accessRole.name : "unknown";

            let clientPath: string = this.clientId;
            if (clientPath.endsWith("-who")) {
                clientPath = clientPath.substr(0, clientPath.length - "-who".length);
            }

            console.log("Client path: " + clientPath);

            const data: any = {
                app_id: this.intercomAppId,
                name: this.pendingUserUpdate.firstName + " " + this.pendingUserUpdate.lastName,
                user_id: this.userId,
                alignment: "right",
                horizontal_padding: 20,
                vertical_padding: 20,
                accessRole: accessRole,
                hide_default_launcher: this.hidden,
                fedelta_version: this.wboVersion,
                company: {
                    id: this.clientId,
                    name: clientPath,
                    client_path: clientPath,
                    fedelta_version: this.wboVersion
                }
            };

            if (this.pendingUserUpdate.employee && this.pendingUserUpdate.employee.contact) {
                if (this.pendingUserUpdate.employee.contact.email) {
                    data.email = this.pendingUserUpdate.employee.contact.email;
                }

                if (this.pendingUserUpdate.employee.contact.mobilePhone || this.pendingUserUpdate.employee.contact.workPhone) {
                    data.phone = this.pendingUserUpdate.employee.contact.mobilePhone ? this.pendingUserUpdate.employee.contact.mobilePhone : this.pendingUserUpdate.employee.contact.workPhone;
                }

                data.employee_number = this.pendingUserUpdate.employee.employeeId;
                if (this.pendingUserUpdate.employee.employeeId === 1) {
                    data.Account_Owner = true;
                }
            }

            console.log("Boot intercom");
            this.pendingUserUpdate = undefined;
            this.started = true;
            window.Intercom("boot", data);
        }
    }

    /**
     * Terminates the intercom session after a user has logged out
     */
    private endUserSession(): void {
        if (this.started) {
            console.log("Shutdown intercom");
            this.userId = undefined;
            this.pendingUserUpdate = undefined;
            this.started = false;
            window.Intercom("shutdown");
        }
    }

}
